import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Project } from "../types/inspector.types";
import CscSubLogo from "Assets/Img/CscSubLogo";
import BellIcons from "Assets/Img/BellIcon";
import useLogout from "../../../../hooks/useLogout";
import useSwitchBackToAdmin from "../../../../hooks/useSwitchBackToAdmin";
import { isImpersonating } from "../../../../utils/Common/impersonation";
import { getUnreadCount } from "../../../../services/notificationService";
import useRefetchOnFocus from "../../../../hooks/useRefetchOnFocus";
import Button from "Components/Common/Button/Button";

interface TopBarProps {
  project: Project;
  onDirections: () => void;
  onSwitch: () => void;
}

export default function TopBar({
  project,
  onDirections,
  onSwitch,
}: TopBarProps) {
  const { handleLogout, loggingOut } = useLogout();
  const { switchBackToAdmin } = useSwitchBackToAdmin();
  const [showBackToAdmin] = useState(isImpersonating());
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [countLoading, setCountLoading] = useState(true);

  const stored = JSON.parse(localStorage.getItem("user") || "{}") as Record<
    string,
    string
  >;
  const userInitials =
    `${stored.firstName?.[0] ?? ""}${stored.lastName?.[0] ?? ""}`.toUpperCase() ||
    "U";

  const fetchCount = async () => {
    setCountLoading(true);
    try {
      const res: any = await getUnreadCount();
      setUnreadCount(res?.data?.data?.count ?? 0);
    } catch {
    } finally {
      setCountLoading(false);
    }
  };

  useRefetchOnFocus(fetchCount);

  useEffect(() => {
    fetchCount();
    const handler = () => {
      fetchCount();
    };
    window.addEventListener("notification-unread-count-changed", handler);
    return () =>
      window.removeEventListener("notification-unread-count-changed", handler);
  }, []);

  return (
    <div className="topBar">
      <div
        className="tbLogo"
        onClick={() => navigate("/project-inspection")}
        style={{ cursor: "pointer" }}
      >
        <CscSubLogo />
      </div>
      <div className="tbInfo">
        {project.name && (
          <div className="tbTitle">
            {project.name} <span className="tbTypeBadge">{project.type}</span>
          </div>
        )}
        <div className="tbSub">{project.sub}</div>
      </div>
      <div className="tbRight">
        <Button
          className="projSwitch"
          onClick={onDirections}
          aria-label="Get directions"
        >
          📍 Directions
        </Button>
        <Button
          className="projSwitch"
          onClick={onSwitch}
          aria-label="Switch project"
        >
          ⇄ Switch
        </Button>
        <div
          className="notifySec"
          onClick={() => navigate("/inspector-notifications")}
          style={{ cursor: "pointer" }}
        >
          <BellIcons />
          {countLoading ? (
            <div className="bellbadgeWrapper">
              <div className="bellbadge">
                <span className="btn-spinner" />
              </div>
            </div>
          ) : unreadCount > 0 ? (
            <div className="bellbadgeWrapper">
              <div className="bellbadge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </div>
            </div>
          ) : null}
        </div>
        <div
          className="navAvatar"
          onClick={() => navigate("/profile")}
          style={{ cursor: "pointer" }}
          title="My Profile"
        >
          {userInitials}
        </div>
        {showBackToAdmin && (
          <Button className="backToAdminBtn" onClick={switchBackToAdmin}>
            Back to Admin
          </Button>
        )}
        <Button
          className="signoutBtn"
          onClick={handleLogout}
          disabled={loggingOut}
        >
          {loggingOut && <span className="btn-spinner" />}{" "}
          {loggingOut ? "Signing out" : "Sign Out"}
        </Button>
      </div>
    </div>
  );
}
